import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Loader2, Download, LogOut, Users, FileSpreadsheet, CheckCircle, Image, Camera, QrCode, Trash2, FileText, Filter, X, BarChart3, RotateCcw, Eye, EyeOff, IndianRupee, Hotel } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHART_COLORS = ['#FF6F4A', '#FFB6C1', '#8B5CF6', '#10B981', '#F59E0B', '#3B82F6', '#EF4444', '#06B6D4', '#EC4899', '#84CC16', '#F97316', '#6366F1'];

// City name normalization map — add common misspellings here
const CITY_CORRECTIONS = {
  'ahemdabad': 'Ahmedabad', 'amdavad': 'Ahmedabad', 'ahmedabad': 'Ahmedabad', 'ahmadabad': 'Ahmedabad',
  'surat': 'Surat', 'suart': 'Surat', 'surta': 'Surat',
  'vadodara': 'Vadodara', 'baroda': 'Vadodara', 'vadodra': 'Vadodara', 'vododara': 'Vadodara',
  'rajkot': 'Rajkot',
  'mumbai': 'Mumbai', 'bombay': 'Mumbai', 'mubmai': 'Mumbai',
  'gandhinagar': 'Gandhinagar', 'gandinagar': 'Gandhinagar',
  'bhavnagar': 'Bhavnagar', 'bhawnagar': 'Bhavnagar',
  'jamnagar': 'Jamnagar', 'jamnangar': 'Jamnagar',
  'junagadh': 'Junagadh', 'junagarh': 'Junagadh',
  'mehsana': 'Mehsana', 'mahesana': 'Mehsana',
  'navsari': 'Navsari',
  'anand': 'Anand', 'aanand': 'Anand',
  'bharuch': 'Bharuch', 'broach': 'Bharuch',
  'valsad': 'Valsad', 'vapi': 'Vapi',
  'delhi': 'Delhi', 'new delhi': 'Delhi',
  'pune': 'Pune', 'poona': 'Pune',
  'hyderabad': 'Hyderabad', 'secunderabad': 'Hyderabad',
  'bangalore': 'Bangalore', 'bengaluru': 'Bangalore',
  'chennai': 'Chennai', 'madras': 'Chennai',
  'kolkata': 'Kolkata', 'calcutta': 'Kolkata',
};

function normalizeCity(city) {
  if (!city) return '';
  const trimmed = city.trim();
  const lower = trimmed.toLowerCase();
  if (CITY_CORRECTIONS[lower]) return CITY_CORRECTIONS[lower];
  // Title case if no correction found
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

const CATEGORY_LABELS = {
  'gos_early_bird': 'GOS Early Bird',
  'non_gos_early_bird': 'Non-GOS Early Bird',
  'applied_gos_early_bird': 'Applied GOS Early Bird',
  'spouse_resident_early_bird': 'Spouse/Resident Early Bird',
  'gos_regular': 'GOS Regular',
  'non_gos_regular': 'Non-GOS Regular',
  'applied_gos_regular': 'Applied GOS Regular',
  'spouse_resident_regular': 'Spouse/Resident Regular',
  'senior_75_complimentary': 'Senior 75+ (Free)',
  'senior_70_complimentary': 'Senior 70+ (Free)',
  'delegate': 'Delegate',
};

const CHECKIN_LABELS = {
  registered: 'Reg',
  breakfast_24: 'BF24',
  lunch_24: 'LN24',
  dinner_24: 'DN24',
  breakfast_25: 'BF25',
  lunch_25: 'LN25',
  kit: 'Kit',
  certificate: 'Cert',
};
const CHECKIN_KEYS = Object.keys(CHECKIN_LABELS);

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const [loading, setLoading] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [abstracts, setAbstracts] = useState([]);
  const [activeTab, setActiveTab] = useState('registrations');
  const [exporting, setExporting] = useState(false);
  const [exportingAbstracts, setExportingAbstracts] = useState(false);
  const [pin, setPin] = useState('');
  const [token, setToken] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCity, setFilterCity] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterAbstractType, setFilterAbstractType] = useState('all');
  const [sortAbstracts, setSortAbstracts] = useState('date');
  const [showDupRegsOnly, setShowDupRegsOnly] = useState(false);
  const [showDupAbsOnly, setShowDupAbsOnly] = useState(false);
  const [visibleCols, setVisibleCols] = useState({
    email: true, phone: true, city: true, type: true, amount: true,
    gos_entered: false, gos_db: false, upi_txn: false,
    photo: false, screenshot: false, qr: false,
    checkins: false, receipt: true, date: true,
  });
  const [showColPicker, setShowColPicker] = useState(false);
  const [exportingFinance, setExportingFinance] = useState(false);
  const [exportingPhotos, setExportingPhotos] = useState(false);
  const [exportingIdCards, setExportingIdCards] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [accommodations, setAccommodations] = useState([]);

  const authHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  useEffect(() => {
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      setToken(savedToken);
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${savedToken}` } })
        .then(() => {
          setIsAuthenticated(true);
          setChecking(false);
        })
        .catch(() => {
          localStorage.removeItem('admin_token');
          setChecking(false);
        });
    } else {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchRegistrations();
      fetchAbstracts();
      fetchAccommodations();
    }
  }, [isAuthenticated, token]);

  const fetchRegistrations = async () => {
    try {
      const res = await axios.get(`${API}/admin/registrations`, { headers: authHeaders() });
      setRegistrations(res.data);
    } catch {
      toast.error('Failed to load registrations');
    }
  };

  const fetchAbstracts = async () => {
    try {
      const res = await axios.get(`${API}/admin/abstracts-full`, { headers: authHeaders() });
      setAbstracts(res.data);
    } catch {
      toast.error('Failed to load abstracts');
    }
  };

  const fetchAccommodations = async () => {
    try {
      const res = await axios.get(`${API}/admin/accommodation`, { headers: authHeaders() });
      setAccommodations(res.data);
    } catch { /* silently fail */ }
  };

  const handleExportFinance = async () => {
    setExportingFinance(true);
    try {
      const res = await axios.get(`${API}/admin/finance/export`, { headers: authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AGOC2026_Finance_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Finance Excel downloaded!');
    } catch {
      toast.error('Finance export failed');
    } finally {
      setExportingFinance(false);
    }
  };

  const handleAbstractUpdate = async (abstractId, updates) => {
    try {
      await axios.post(`${API}/admin/abstract/update`, { abstract_id: abstractId, ...updates }, { headers: authHeaders() });
      toast.success('Abstract updated');
      fetchAbstracts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const handleVerifyPayment = async (registrationId) => {
    if (!window.confirm('Mark this payment as verified? A confirmation email will be sent to the delegate.')) return;
    try {
      const res = await axios.post(`${API}/admin/verify-payment`, { registration_id: registrationId }, { headers: authHeaders() });
      const data = res.data;
      if (data.status === 'partial') {
        toast.warning(data.message);
      } else {
        toast.success(data.message);
      }
      fetchRegistrations();
    } catch {
      toast.error('Verification failed');
    }
  };

  const handleResendEmail = async (registrationId) => {
    try {
      const res = await axios.post(`${API}/admin/resend-confirmation`, { registration_id: registrationId }, { headers: authHeaders() });
      const data = res.data;
      if (data.status === 'partial') {
        toast.warning(data.message);
      } else {
        toast.success(data.message);
      }
    } catch {
      toast.error('Failed to resend email');
    }
  };

  const handleDeleteRegistration = async (registrationId, name) => {
    const deletePin = window.prompt(`Enter delete PIN to remove "${name}":`);
    if (!deletePin) return;
    try {
      await axios.post(`${API}/admin/delete-registration`, { registration_id: registrationId, delete_pin: deletePin }, { headers: authHeaders() });
      toast.success(`Registration for "${name}" deleted`);
      fetchRegistrations();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Delete failed');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { pin });
      const data = res.data;
      if (data.token) {
        setToken(data.token);
        localStorage.setItem('admin_token', data.token);
      }
      setIsAuthenticated(true);
      toast.success('Login successful');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Invalid PIN');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken('');
    setIsAuthenticated(false);
    setRegistrations([]);
    setPin('');
    toast.success('Logged out');
  };

  const handleGenerateAllQR = async () => {
    try {
      const res = await axios.post(`${API}/admin/generate-all-qr`, {}, { headers: authHeaders() });
      toast.success(res.data.message);
      fetchRegistrations();
    } catch {
      toast.error('Failed to generate QR codes');
    }
  };

  const handleExportPhotos = async () => {
    setExportingPhotos(true);
    try {
      const res = await axios.get(`${API}/admin/photos/export`, { headers: authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AGOC2026_Delegate_Photos_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Delegate photos Excel downloaded!');
    } catch {
      toast.error('Photos export failed');
    } finally {
      setExportingPhotos(false);
    }
  };

  const handleDownloadPhotosZip = async () => {
    setDownloadingZip(true);
    try {
      const res = await axios.get(`${API}/admin/photos/download-zip`, { headers: authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AGOC2026_Delegate_Photos_${new Date().toISOString().slice(0,10)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Photos ZIP downloaded!');
    } catch {
      toast.error('Photos ZIP download failed');
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleExportIdCards = async () => {
    setExportingIdCards(true);
    try {
      const res = await axios.get(`${API}/admin/id-cards/export`, { headers: authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AGOC2026_ID_Cards_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('ID cards PDF downloaded!');
    } catch {
      toast.error('ID cards export failed');
    } finally {
      setExportingIdCards(false);
    }
  };

  const handleSingleIdCard = async (regId, name) => {
    try {
      const res = await axios.get(`${API}/admin/id-card/${regId}`, { headers: authHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AGOC2026_IDCard_${(name || 'delegate').replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('ID card downloaded!');
    } catch {
      toast.error('ID card download failed');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/registrations/export`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AGOC2026_Registrations_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel file downloaded!');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const formatAmount = (paise) => {
    return `₹${(paise / 100).toLocaleString('en-IN')}`;
  };

  const handleExportAbstracts = async () => {
    setExportingAbstracts(true);
    try {
      const res = await axios.get(`${API}/admin/abstracts/export`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AGOC2026_Abstracts_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Abstracts Excel downloaded!');
    } catch {
      toast.error('Export failed');
    } finally {
      setExportingAbstracts(false);
    }
  };

  const handleResetCheckin = async (registrationId, checkinKey) => {
    if (!window.confirm(`Reset "${CHECKIN_LABELS[checkinKey]}" for this delegate?`)) return;
    try {
      await axios.post(`${API}/admin/reset-checkin`, { registration_id: registrationId, checkin_key: checkinKey }, { headers: authHeaders() });
      toast.success(`Reset ${CHECKIN_LABELS[checkinKey]}`);
      fetchRegistrations();
    } catch {
      toast.error('Reset failed');
    }
  };

  const [sendingReceipt, setSendingReceipt] = useState('');

  const handleSendReceipt = async (registrationId) => {
    setSendingReceipt(registrationId);
    try {
      const res = await axios.post(`${API}/admin/send-receipt`, { registration_id: registrationId }, { headers: authHeaders() });
      if (res.data.status === 'partial') {
        toast.warning(res.data.message);
      } else {
        toast.success(res.data.message);
      }
      fetchRegistrations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send receipt');
    } finally {
      setSendingReceipt('');
    }
  };

  const handleDownloadReceipt = async (registrationId) => {
    try {
      const res = await axios.get(`${API}/admin/download-receipt/${registrationId}`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `AGOC2026_Receipt.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Normalize city names in registrations
  const normalizedRegistrations = useMemo(() => {
    return registrations.map(r => ({
      ...r,
      city: normalizeCity(r.city)
    }));
  }, [registrations]);

  // Derive unique cities and types for filter dropdowns
  const uniqueCities = [...new Set(normalizedRegistrations.map(r => r.city || '').filter(Boolean))].sort();
  const uniqueTypes = [...new Set(normalizedRegistrations.map(r => r.registration_type || '').filter(Boolean))].sort();

  // --- Duplicate detection helpers ---
  const normKey = (s) => (s || '').toString().trim().toLowerCase();
  const normPhone = (s) => (s || '').toString().replace(/\D/g, '').slice(-10);

  const dupAbstractEmails = useMemo(() => {
    const counts = {};
    abstracts.forEach(a => { const k = normKey(a.email); if (k) counts[k] = (counts[k] || 0) + 1; });
    return new Set(Object.keys(counts).filter(k => counts[k] > 1));
  }, [abstracts]);

  const dupRegKeys = useMemo(() => {
    const emailCounts = {}, phoneCounts = {};
    registrations.forEach(r => {
      const e = normKey(r.email); if (e) emailCounts[e] = (emailCounts[e] || 0) + 1;
      const p = normPhone(r.phone); if (p && p.length === 10) phoneCounts[p] = (phoneCounts[p] || 0) + 1;
    });
    return {
      emails: new Set(Object.keys(emailCounts).filter(k => emailCounts[k] > 1)),
      phones: new Set(Object.keys(phoneCounts).filter(k => phoneCounts[k] > 1)),
    };
  }, [registrations]);

  const isDupReg = (r) => dupRegKeys.emails.has(normKey(r.email)) || dupRegKeys.phones.has(normPhone(r.phone));
  const dupRegCount = useMemo(() => normalizedRegistrations.filter(isDupReg).length, [normalizedRegistrations, dupRegKeys]);

  // Apply filters
  const filteredRegistrations = normalizedRegistrations.filter(reg => {
    if (showDupRegsOnly && !isDupReg(reg)) return false;
    if (filterStatus === 'unverified' && reg.payment_status === 'completed') return false;
    if (filterStatus === 'verified' && reg.payment_status !== 'completed') return false;
    if (filterCity !== 'all' && (reg.city || '') !== filterCity) return false;
    if (filterType !== 'all' && reg.registration_type !== filterType) return false;
    if (filterDateFrom) {
      const regDate = new Date(reg.created_at).toISOString().slice(0, 10);
      if (regDate < filterDateFrom) return false;
    }
    if (filterDateTo) {
      const regDate = new Date(reg.created_at).toISOString().slice(0, 10);
      if (regDate > filterDateTo) return false;
    }
    return true;
  });

  // When showing duplicates only, group copies together (by email then phone)
  if (showDupRegsOnly) {
    filteredRegistrations.sort((a, b) => {
      const ea = normKey(a.email), eb = normKey(b.email);
      if (ea !== eb) return ea.localeCompare(eb);
      return normPhone(a.phone).localeCompare(normPhone(b.phone));
    });
  }

  // Chart data
  const cityChartData = useMemo(() => {
    const counts = {};
    normalizedRegistrations.forEach(r => {
      const c = r.city || 'Unknown';
      counts[c] = (counts[c] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [normalizedRegistrations]);

  const categoryChartData = useMemo(() => {
    const counts = {};
    normalizedRegistrations.forEach(r => {
      const t = r.registration_type || 'Unknown';
      counts[t] = (counts[t] || 0) + 1;
    });
    return Object.entries(counts).map(([key, value]) => ({ name: CATEGORY_LABELS[key] || key.replace(/_/g, ' '), value })).sort((a, b) => b.value - a.value);
  }, [normalizedRegistrations]);

  const statusChartData = useMemo(() => {
    let verified = 0, unverified = 0;
    normalizedRegistrations.forEach(r => {
      if (r.payment_status === 'completed') verified++;
      else unverified++;
    });
    return [
      { name: 'Verified', value: verified },
      { name: 'Unverified', value: unverified },
    ];
  }, [normalizedRegistrations]);

  const hasActiveFilters = filterStatus !== 'all' || filterCity !== 'all' || filterType !== 'all' || filterDateFrom || filterDateTo || showDupRegsOnly;

  const clearAllFilters = () => {
    setFilterStatus('all');
    setFilterCity('all');
    setFilterType('all');
    setFilterDateFrom('');
    setFilterDateTo('');
    setShowDupRegsOnly(false);
  };

  // --- Abstract filter + sort ---
  const filteredAbstracts = useMemo(() => {
    let list = abstracts.filter(ab => {
      if (showDupAbsOnly && !dupAbstractEmails.has(normKey(ab.email))) return false;
      const t = ab.presentation_type;
      if (filterAbstractType === 'all') return true;
      if (filterAbstractType === 'undecided') return !t || t === 'none';
      return t === filterAbstractType;
    });
    list = [...list];
    if (showDupAbsOnly) {
      list.sort((a, b) => normKey(a.email).localeCompare(normKey(b.email)));
    } else if (sortAbstracts === 'author_az') list.sort((a, b) => (a.author_name || '').localeCompare(b.author_name || ''));
    else if (sortAbstracts === 'title_az') list.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    else list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list;
  }, [abstracts, filterAbstractType, sortAbstracts, showDupAbsOnly, dupAbstractEmails]);

  if (checking) {
    return (
      <div className="min-h-screen pt-32 pb-16 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF6F4A]" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-32 pb-16" data-testid="admin-login-page">
        <div className="max-w-sm mx-auto px-6">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-[#FF6F4A] to-[#FFB6C1] flex items-center justify-center">
              <Lock className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold mb-2" data-testid="admin-title">
              Admin <span className="gradient-text">Panel</span>
            </h1>
            <p className="text-slate-400">AGOC 2026 Registration Management</p>
          </div>

          <form onSubmit={handleLogin} className="glass-card p-8" data-testid="admin-login-form">
            <div className="space-y-5">
              <div>
                <Label htmlFor="pin" className="text-white">Enter PIN</Label>
                <Input
                  id="pin"
                  type="password"
                  required
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  className="glass-card border-white/20 text-white mt-2 text-center text-2xl tracking-widest"
                  placeholder="Enter PIN"
                  data-testid="admin-pin-input"
                  autoComplete="off"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FF6F4A] hover:bg-[#FF6F4A]/90 text-white font-semibold py-5"
                data-testid="admin-login-button"
              >
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
                {loading ? 'Verifying...' : 'Login'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 pb-16" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold" data-testid="admin-dashboard-title">
              Admin <span className="gradient-text">Dashboard</span>
            </h1>
            <p className="text-slate-400 mt-1">AGOC 2026 Registration Management</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button onClick={handleExport} disabled={exporting} className="bg-green-600 hover:bg-green-700 text-white font-semibold" data-testid="export-excel-button">
              {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              {exporting ? 'Exporting...' : 'Download Excel'}
            </Button>
            <Button onClick={handleExportFinance} disabled={exportingFinance} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="export-finance-button">
              {exportingFinance ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <IndianRupee className="mr-2 h-4 w-4" />}
              {exportingFinance ? 'Exporting...' : 'Finance Excel'}
            </Button>
            <Button onClick={handleExportPhotos} disabled={exportingPhotos} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold" data-testid="export-photos-button">
              {exportingPhotos ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              {exportingPhotos ? 'Exporting...' : 'Photos Excel'}
            </Button>
            <Button onClick={handleDownloadPhotosZip} disabled={downloadingZip} className="bg-teal-600 hover:bg-teal-700 text-white font-semibold" data-testid="download-photos-zip-button">
              {downloadingZip ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              {downloadingZip ? 'Zipping...' : 'All Photos (ZIP)'}
            </Button>
            <Button onClick={handleExportIdCards} disabled={exportingIdCards} className="bg-purple-600 hover:bg-purple-700 text-white font-semibold" data-testid="export-idcards-button">
              {exportingIdCards ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              {exportingIdCards ? 'Generating...' : 'ID Cards (PDF)'}
            </Button>
            <Button
              onClick={handleGenerateAllQR}
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold"
              data-testid="generate-qr-button"
            >
              <QrCode className="mr-2 h-4 w-4" />
              Generate QR Codes
            </Button>
            <Button
              onClick={fetchRegistrations}
              variant="outline"
              className="glass-card text-white border-white/20"
              data-testid="refresh-button"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="glass-card text-white border-white/20 hover:bg-red-500/20"
              data-testid="logout-button"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        <div className="glass-card p-6 mb-6">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <Users className="h-6 w-6 text-[#FF6F4A]" />
              <span className="text-2xl font-bold" data-testid="total-registrations">{registrations.length}</span>
              <span className="text-slate-400">Registrations</span>
            </div>
            <div className="w-px h-8 bg-white/20" />
            <div className="flex items-center gap-3">
              <FileText className="h-6 w-6 text-purple-400" />
              <span className="text-2xl font-bold" data-testid="total-abstracts">{abstracts.length}</span>
              <span className="text-slate-400">Abstracts</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6" data-testid="admin-tabs">
          <button
            onClick={() => setActiveTab('registrations')}
            className={`px-5 py-2.5 rounded-lg font-semibold text-sm transition-all ${
              activeTab === 'registrations'
                ? 'bg-[#FF6F4A] text-white'
                : 'glass-card text-slate-300 hover:text-white hover:bg-white/10'
            }`}
            data-testid="tab-registrations"
          >
            <Users className="h-4 w-4 inline mr-2" />
            Registrations ({registrations.length})
          </button>
          <button
            onClick={() => setActiveTab('abstracts')}
            className={`px-5 py-2.5 rounded-lg font-semibold text-sm transition-all ${
              activeTab === 'abstracts'
                ? 'bg-purple-600 text-white'
                : 'glass-card text-slate-300 hover:text-white hover:bg-white/10'
            }`}
            data-testid="tab-abstracts"
          >
            <FileText className="h-4 w-4 inline mr-2" />
            Abstracts ({abstracts.length})
          </button>
          <button
            onClick={() => setActiveTab('charts')}
            className={`px-5 py-2.5 rounded-lg font-semibold text-sm transition-all ${
              activeTab === 'charts'
                ? 'bg-emerald-600 text-white'
                : 'glass-card text-slate-300 hover:text-white hover:bg-white/10'
            }`}
            data-testid="tab-charts"
          >
            <BarChart3 className="h-4 w-4 inline mr-2" />
            Charts
          </button>
          <button
            onClick={() => setActiveTab('accommodation')}
            className={`px-5 py-2.5 rounded-lg font-semibold text-sm transition-all ${
              activeTab === 'accommodation'
                ? 'bg-amber-600 text-white'
                : 'glass-card text-slate-300 hover:text-white hover:bg-white/10'
            }`}
            data-testid="tab-accommodation"
          >
            <Hotel className="h-4 w-4 inline mr-2" />
            Accommodation ({accommodations.length})
          </button>
        </div>

        {/* Filters - only show on registrations tab */}
        {activeTab === 'registrations' && (
          <div className="glass-card p-4 mb-6" data-testid="filter-bar">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="h-4 w-4 text-[#FF6F4A]" />
              <span className="text-sm font-semibold text-slate-300">Filters</span>
              {hasActiveFilters && (
                <button onClick={clearAllFilters} className="ml-auto flex items-center gap-1 text-xs text-red-400 hover:text-red-300" data-testid="clear-filters-btn">
                  <X className="h-3 w-3" /> Clear All
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF6F4A]"
                  data-testid="filter-status"
                >
                  <option value="all" className="bg-slate-800">All</option>
                  <option value="unverified" className="bg-slate-800">Unverified</option>
                  <option value="verified" className="bg-slate-800">Verified</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">City</label>
                <select
                  value={filterCity}
                  onChange={(e) => setFilterCity(e.target.value)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF6F4A]"
                  data-testid="filter-city"
                >
                  <option value="all" className="bg-slate-800">All Cities</option>
                  {uniqueCities.map(city => (
                    <option key={city} value={city} className="bg-slate-800">{city}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Category</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF6F4A]"
                  data-testid="filter-type"
                >
                  <option value="all" className="bg-slate-800">All Categories</option>
                  {uniqueTypes.map(type => (
                    <option key={type} value={type} className="bg-slate-800">{type.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">From Date</label>
                <input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF6F4A]"
                  data-testid="filter-date-from"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">To Date</label>
                <input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF6F4A]"
                  data-testid="filter-date-to"
                />
              </div>
            </div>
            {hasActiveFilters && (
              <p className="text-xs text-slate-400 mt-2" data-testid="filter-count">
                Showing <span className="text-white font-semibold">{filteredRegistrations.length}</span> of {registrations.length} registrations
              </p>
            )}
            {dupRegCount > 0 && (
              <p className="text-xs mt-2">
                <button
                  type="button"
                  onClick={() => setShowDupRegsOnly(v => !v)}
                  className={`px-2 py-1 rounded-full font-semibold transition-all ${showDupRegsOnly ? 'bg-red-500 text-white' : 'bg-red-500/20 text-red-300 hover:bg-red-500/30'}`}
                  data-testid="registrations-dup-toggle">
                  {showDupRegsOnly ? `Showing ${dupRegCount} duplicate(s) — click to show all` : `Show ${dupRegCount} duplicate registration(s)`}
                </button>
              </p>
            )}
            {/* Column Visibility */}
            <div className="mt-3 pt-3 border-t border-white/10">
              <button onClick={() => setShowColPicker(!showColPicker)} className="flex items-center gap-1 text-xs text-slate-400 hover:text-white" data-testid="toggle-columns-btn">
                {showColPicker ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                {showColPicker ? 'Hide' : 'Show'} Column Options
              </button>
              {showColPicker && (
                <div className="flex flex-wrap gap-2 mt-2" data-testid="column-picker">
                  {Object.entries({
                    email: 'Email', phone: 'Phone', city: 'City', type: 'Type', amount: 'Amount',
                    gos_entered: 'GOS (Entered)', gos_db: 'GOS (DB)', upi_txn: 'UPI TXN',
                    photo: 'Photo', screenshot: 'Screenshot', qr: 'QR Code',
                    checkins: 'Check-ins', receipt: 'Receipt', date: 'Date',
                  }).map(([key, label]) => (
                    <label key={key} className="flex items-center gap-1 text-[10px] text-slate-300 cursor-pointer">
                      <input type="checkbox" checked={visibleCols[key]} onChange={() => setVisibleCols(prev => ({ ...prev, [key]: !prev[key] }))}
                        className="w-3 h-3 rounded accent-[#FF6F4A]" />
                      {label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'charts' ? (
        <div className="space-y-6" data-testid="charts-section">
          {/* Status Pie + City Pie side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Payment Status</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={statusChartData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}>
                    {statusChartData.map((_, i) => (
                      <Cell key={i} fill={i === 0 ? '#10B981' : '#F59E0B'} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-white mb-4">City-wise Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={cityChartData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {cityChartData.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                  <Legend wrapperStyle={{ color: '#94a3b8', fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Category Bar Chart */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Category-wise Registrations</h3>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={categoryChartData} margin={{ top: 5, right: 30, left: 20, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fill: '#94a3b8' }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {categoryChartData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* City Bar Chart */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-white mb-4">City-wise Registrations</h3>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={cityChartData} margin={{ top: 5, right: 30, left: 20, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
                <YAxis tick={{ fill: '#94a3b8' }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {cityChartData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        ) : activeTab === 'accommodation' ? (
        <div className="glass-card overflow-hidden" data-testid="accommodation-section">
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <span className="text-sm text-slate-400">{accommodations.length} request(s)</span>
            <Button onClick={fetchAccommodations} size="sm" variant="outline" className="glass-card text-white border-white/20 text-xs">Refresh</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-white/20 bg-white/5">
                  <th className="py-3 px-4 font-semibold text-slate-300">#</th>
                  <th className="py-3 px-4 font-semibold text-slate-300">Delegate</th>
                  <th className="py-3 px-4 font-semibold text-slate-300">Phone</th>
                  <th className="py-3 px-4 font-semibold text-slate-300">Email</th>
                  <th className="py-3 px-4 font-semibold text-slate-300">Check-in</th>
                  <th className="py-3 px-4 font-semibold text-slate-300">Check-out</th>
                  <th className="py-3 px-4 font-semibold text-slate-300">Persons</th>
                  <th className="py-3 px-4 font-semibold text-slate-300">Room Type</th>
                  <th className="py-3 px-4 font-semibold text-slate-300">Requirements</th>
                  <th className="py-3 px-4 font-semibold text-slate-300">Date</th>
                </tr>
              </thead>
              <tbody>
                {accommodations.length === 0 ? (
                  <tr><td colSpan={10} className="py-12 text-center text-slate-400">No accommodation requests</td></tr>
                ) : accommodations.map((acc, i) => (
                  <tr key={acc.id || i} className="border-b border-white/10 hover:bg-white/5">
                    <td className="py-3 px-4 text-slate-400">{i + 1}</td>
                    <td className="py-3 px-4 text-white font-medium">{acc.delegate_name}</td>
                    <td className="py-3 px-4 text-slate-300">{acc.phone}</td>
                    <td className="py-3 px-4 text-slate-300">{acc.email}</td>
                    <td className="py-3 px-4 text-slate-300">{acc.check_in_date}</td>
                    <td className="py-3 px-4 text-slate-300">{acc.check_out_date}</td>
                    <td className="py-3 px-4 text-white font-medium">{acc.num_persons}</td>
                    <td className="py-3 px-4 text-slate-300">{acc.room_type}</td>
                    <td className="py-3 px-4 text-slate-400 text-xs max-w-xs truncate">{acc.special_requirements || '-'}</td>
                    <td className="py-3 px-4 text-slate-400 text-xs">{formatDate(acc.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        ) : activeTab === 'abstracts' ? (
        <div className="glass-card overflow-hidden" data-testid="abstracts-section">
          <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-white/10">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-slate-400" data-testid="abstracts-count">
                Showing {filteredAbstracts.length} of {abstracts.length} abstract(s)
              </span>
              {dupAbstractEmails.size > 0 && (
                <button
                  type="button"
                  onClick={() => setShowDupAbsOnly(v => !v)}
                  className={`text-[11px] px-2 py-1 rounded-full font-semibold transition-all ${showDupAbsOnly ? 'bg-red-500 text-white' : 'bg-red-500/20 text-red-300 hover:bg-red-500/30'}`}
                  data-testid="abstracts-dup-toggle">
                  {showDupAbsOnly ? `Showing duplicates — click to show all` : `Show ${dupAbstractEmails.size} duplicate(s)`}
                </button>
              )}
              <div className="flex items-center gap-1">
                <label className="text-xs text-slate-400">Type:</label>
                <select value={filterAbstractType} onChange={(e) => setFilterAbstractType(e.target.value)}
                  className="bg-white/5 border border-white/20 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-[#FF6F4A]"
                  data-testid="abstract-filter-type">
                  <option value="all" className="bg-slate-800">All</option>
                  <option value="paper" className="bg-slate-800">Paper</option>
                  <option value="poster" className="bg-slate-800">Poster</option>
                  <option value="undecided" className="bg-slate-800">Undecided</option>
                </select>
              </div>
              <div className="flex items-center gap-1">
                <label className="text-xs text-slate-400">Sort:</label>
                <select value={sortAbstracts} onChange={(e) => setSortAbstracts(e.target.value)}
                  className="bg-white/5 border border-white/20 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-[#FF6F4A]"
                  data-testid="abstract-sort">
                  <option value="date" className="bg-slate-800">Newest first</option>
                  <option value="author_az" className="bg-slate-800">Author (A–Z)</option>
                  <option value="title_az" className="bg-slate-800">Title (A–Z)</option>
                </select>
              </div>
            </div>
            <Button onClick={handleExportAbstracts} disabled={exportingAbstracts} size="sm"
              className="bg-green-600 hover:bg-green-700 text-white font-semibold text-xs" data-testid="export-abstracts-button">
              {exportingAbstracts ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Download className="mr-2 h-3 w-3" />}
              {exportingAbstracts ? 'Exporting...' : 'Download Abstracts Excel'}
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="abstracts-table">
              <thead>
                <tr className="border-b border-white/20 bg-white/5">
                  <th className="py-3 px-3 font-semibold text-slate-300">#</th>
                  <th className="py-3 px-3 font-semibold text-slate-300">Author</th>
                  <th className="py-3 px-3 font-semibold text-slate-300">Action</th>
                  <th className="py-3 px-3 font-semibold text-slate-300">Type</th>
                  <th className="py-3 px-3 font-semibold text-slate-300">Email</th>
                  <th className="py-3 px-3 font-semibold text-slate-300">Phone</th>
                  <th className="py-3 px-3 font-semibold text-slate-300">Title</th>
                  <th className="py-3 px-3 font-semibold text-slate-300">Category</th>
                  <th className="py-3 px-3 font-semibold text-slate-300">Abstract</th>
                  <th className="py-3 px-3 font-semibold text-slate-300">Status</th>
                  <th className="py-3 px-3 font-semibold text-slate-300">Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredAbstracts.length === 0 ? (
                  <tr><td colSpan={11} className="py-12 text-center text-slate-400">No abstract submissions match this view</td></tr>
                ) : (
                  filteredAbstracts.map((ab, idx) => (
                    <tr key={ab.id || idx} className="border-b border-white/10 hover:bg-white/5 transition-colors" data-testid={`abstract-row-${idx}`}>
                      <td className="py-2 px-3 text-slate-400">{idx + 1}</td>
                      <td className="py-2 px-3 text-white font-medium text-xs">
                        <div className="flex items-center gap-1">
                          <span>{ab.author_name}</span>
                          {dupAbstractEmails.has(normKey(ab.email)) && (
                            <span className="text-[9px] px-1 py-0.5 rounded bg-red-500/30 text-red-300 font-semibold" title="Duplicate email — multiple abstracts from this address" data-testid={`abstract-dup-${idx}`}>DUP</span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1">
                          {ab.status !== 'accepted' && (
                            <button onClick={() => handleAbstractUpdate(ab.id, { status: 'accepted' })}
                              className="text-[10px] px-1.5 py-0.5 rounded bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-abstract-${idx}`}>Approve</button>
                          )}
                          {ab.status !== 'rejected' && (
                            <button onClick={() => handleAbstractUpdate(ab.id, { status: 'rejected' })}
                              className="text-[10px] px-1.5 py-0.5 rounded bg-red-600 hover:bg-red-700 text-white" data-testid={`reject-abstract-${idx}`}>Reject</button>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <select value={ab.presentation_type || 'none'}
                          onChange={(e) => handleAbstractUpdate(ab.id, { presentation_type: e.target.value })}
                          className="bg-white/5 border border-white/20 rounded px-1.5 py-1 text-[10px] text-white focus:outline-none focus:border-[#FF6F4A]"
                          data-testid={`abstract-type-${idx}`}>
                          <option value="none" className="bg-slate-800">-</option>
                          <option value="paper" className="bg-slate-800">Paper</option>
                          <option value="poster" className="bg-slate-800">Poster</option>
                        </select>
                      </td>
                      <td className="py-2 px-3 text-slate-300 text-xs">{ab.email}</td>
                      <td className="py-2 px-3 text-slate-300 text-xs">{ab.phone}</td>
                      <td className="py-2 px-3 text-white max-w-xs truncate text-xs" title={ab.title}>{ab.title}</td>
                      <td className="py-2 px-3"><span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-500/20 text-purple-300">{ab.category}</span></td>
                      <td className="py-2 px-3 text-slate-300 max-w-sm">
                        <details className="cursor-pointer"><summary className="text-[10px] text-[#FF6F4A]">View</summary>
                          <p className="mt-2 text-[10px] leading-relaxed whitespace-pre-wrap">{ab.abstract_text}</p>
                        </details>
                      </td>
                      <td className="py-2 px-3">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          ab.status === 'accepted' ? 'bg-green-500/20 text-green-400' :
                          ab.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`}>{ab.status === 'accepted' ? 'Selected' : ab.status === 'rejected' ? 'Rejected' : 'Pending'}</span>
                      </td>
                      <td className="py-2 px-3 text-slate-400 text-[10px]">{formatDate(ab.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        ) : (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="registrations-table">
              <thead>
                <tr className="border-b border-white/20 bg-white/5">
                  <th className="py-3 px-3 font-semibold text-slate-300 sticky left-0 bg-[#1a1040] z-10">#</th>
                  <th className="py-3 px-3 font-semibold text-slate-300">Name</th>
                  <th className="py-3 px-3 font-semibold text-slate-300">Action</th>
                  <th className="py-3 px-3 font-semibold text-slate-300">Status</th>
                  {visibleCols.email && <th className="py-3 px-3 font-semibold text-slate-300">Email</th>}
                  {visibleCols.phone && <th className="py-3 px-3 font-semibold text-slate-300">Phone</th>}
                  {visibleCols.city && <th className="py-3 px-3 font-semibold text-slate-300">City</th>}
                  {visibleCols.type && <th className="py-3 px-3 font-semibold text-slate-300">Type</th>}
                  {visibleCols.amount && <th className="py-3 px-3 font-semibold text-slate-300">Amount</th>}
                  {visibleCols.gos_entered && <th className="py-3 px-3 font-semibold text-slate-300">GOS (Entered)</th>}
                  {visibleCols.gos_db && <th className="py-3 px-3 font-semibold text-slate-300">GOS (DB)</th>}
                  {visibleCols.upi_txn && <th className="py-3 px-3 font-semibold text-slate-300">UPI TXN</th>}
                  {visibleCols.photo && <th className="py-3 px-3 font-semibold text-slate-300">Photo</th>}
                  {visibleCols.screenshot && <th className="py-3 px-3 font-semibold text-slate-300">Screenshot</th>}
                  {visibleCols.qr && <th className="py-3 px-3 font-semibold text-slate-300">QR</th>}
                  {visibleCols.checkins && <th className="py-3 px-3 font-semibold text-slate-300">Check-ins</th>}
                  {visibleCols.receipt && <th className="py-3 px-3 font-semibold text-slate-300">Receipt</th>}
                  {visibleCols.date && <th className="py-3 px-3 font-semibold text-slate-300">Date</th>}
                </tr>
              </thead>
              <tbody>
                {filteredRegistrations.length === 0 ? (
                  <tr>
                    <td colSpan={20} className="py-12 text-center text-slate-400">
                      {hasActiveFilters ? 'No registrations match the selected filters' : 'No registrations yet'}
                    </td>
                  </tr>
                ) : (
                  filteredRegistrations.map((reg, idx) => (
                    <tr key={reg.id || idx} className="border-b border-white/10 hover:bg-white/5 transition-colors" data-testid={`registration-row-${idx}`}>
                      <td className="py-2 px-3 text-slate-400 sticky left-0 bg-[#1a1040]">{idx + 1}</td>
                      <td className="py-2 px-3 text-white font-medium text-xs">
                        <div className="flex items-center gap-1">
                          <span>{reg.full_name}</span>
                          {isDupReg(reg) && (
                            <span className="text-[9px] px-1 py-0.5 rounded bg-red-500/30 text-red-300 font-semibold" title="Duplicate — same email or phone appears in another registration" data-testid={`registration-dup-${idx}`}>DUP</span>
                          )}
                        </div>
                      </td>
                      {/* ACTION COLUMN - FRONT */}
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-1">
                          {reg.payment_status !== 'completed' ? (
                            <Button size="sm" onClick={() => handleVerifyPayment(reg.id)}
                              className="bg-green-600 hover:bg-green-700 text-white text-[10px] px-2 py-0.5 h-6"
                              data-testid={`verify-btn-${idx}`}>
                              <CheckCircle className="h-3 w-3 mr-0.5" /> Verify
                            </Button>
                          ) : (
                            <Button size="sm" onClick={() => handleResendEmail(reg.id)} variant="outline"
                              className="text-[10px] px-2 py-0.5 h-6 border-[#FF6F4A]/30 text-[#FF6F4A] hover:bg-[#FF6F4A]/10"
                              data-testid={`resend-btn-${idx}`}>
                              Resend
                            </Button>
                          )}
                          <Button size="sm" onClick={() => handleDeleteRegistration(reg.id, reg.full_name)} variant="outline"
                            className="text-[10px] px-1.5 py-0.5 h-6 border-red-500/30 text-red-400 hover:bg-red-500/10"
                            data-testid={`delete-btn-${idx}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                          <Button size="sm" onClick={() => handleSingleIdCard(reg.id, reg.full_name)} variant="outline"
                            className="text-[10px] px-1.5 py-0.5 h-6 border-purple-500/30 text-purple-300 hover:bg-purple-500/10"
                            title="Download ID card" data-testid={`idcard-btn-${idx}`}>
                            ID
                          </Button>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          reg.payment_status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                        }`}>{reg.payment_status === 'completed' ? 'Verified' : 'Pending'}</span>
                      </td>
                      {visibleCols.email && <td className="py-2 px-3 text-slate-300 text-xs">{reg.email}</td>}
                      {visibleCols.phone && <td className="py-2 px-3 text-slate-300 text-xs">{reg.phone}</td>}
                      {visibleCols.city && <td className="py-2 px-3 text-slate-300 text-xs">{reg.city || '-'}</td>}
                      {visibleCols.type && <td className="py-2 px-3"><span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FF6F4A]/20 text-[#FF6F4A]">{reg.registration_type?.replace(/_/g, ' ')}</span></td>}
                      {visibleCols.amount && <td className="py-2 px-3 text-white font-medium text-xs">{formatAmount(reg.amount)}</td>}
                      {visibleCols.gos_entered && <td className="py-2 px-3">{reg.gos_membership_number ? <span className="text-xs font-mono text-[#FFB6C1]">{reg.gos_membership_number}</span> : <span className="text-xs text-slate-500">-</span>}</td>}
                      {visibleCols.gos_db && <td className="py-2 px-3">{reg.db_gos_number ? <span className={`text-xs font-mono font-bold ${reg.gos_membership_number && reg.gos_membership_number === reg.db_gos_number ? 'text-green-400' : 'text-yellow-400'}`}>{reg.db_gos_number}</span> : <span className="text-xs text-slate-500">-</span>}</td>}
                      {visibleCols.upi_txn && <td className="py-2 px-3 text-[#FFB6C1] font-mono text-[10px]">{reg.upi_transaction_id || '-'}</td>}
                      {visibleCols.photo && <td className="py-2 px-3">{reg.delegate_photo_url && reg.delegate_photo_url !== 'None' && reg.delegate_photo_url.includes('/') ? <a href={`${BACKEND_URL}/api/file/${reg.delegate_photo_url.split('/').pop()}`} target="_blank" rel="noopener noreferrer" className="text-[#FF6F4A] text-[10px]"><Camera className="h-3 w-3 inline" /></a> : <span className="text-xs text-slate-500">-</span>}</td>}
                      {visibleCols.screenshot && <td className="py-2 px-3">{reg.payment_screenshot_url && reg.payment_screenshot_url !== 'None' && reg.payment_screenshot_url.includes('/') ? <a href={`${BACKEND_URL}/api/file/${reg.payment_screenshot_url.split('/').pop()}`} target="_blank" rel="noopener noreferrer" className="text-[#FF6F4A] text-[10px]"><Image className="h-3 w-3 inline" /></a> : <span className="text-xs text-slate-500">-</span>}</td>}
                      {visibleCols.qr && <td className="py-2 px-3">{reg.qr_code_url && reg.qr_code_url !== 'None' && reg.qr_code_url.includes('/') ? <a href={`${BACKEND_URL}/api/file/${reg.qr_code_url.split('/').pop()}`} target="_blank" rel="noopener noreferrer" className="text-purple-400 text-[10px]"><QrCode className="h-3 w-3 inline" /></a> : <span className="text-xs text-slate-500">-</span>}</td>}
                      {visibleCols.checkins && <td className="py-2 px-3"><div className="flex flex-wrap gap-0.5">{CHECKIN_KEYS.map(key => { const done = !!(reg.event_checkins && reg.event_checkins[key]); return (<span key={key} onClick={done ? () => handleResetCheckin(reg.id, key) : undefined} className={`text-[9px] px-1 py-0 rounded ${done ? 'bg-green-500/30 text-green-300 cursor-pointer' : 'bg-slate-700/50 text-slate-500'}`} title={CHECKIN_LABELS[key]}>{CHECKIN_LABELS[key]}</span>); })}</div></td>}
                      {visibleCols.receipt && <td className="py-2 px-3">{reg.receipt_number ? (<div className="flex items-center gap-1"><span className="text-[9px] text-green-400">{reg.receipt_number}</span><button onClick={() => handleDownloadReceipt(reg.id)} className="text-[9px] text-blue-400 underline" data-testid={`download-receipt-${idx}`}>PDF</button><button onClick={() => handleSendReceipt(reg.id)} className="text-[9px] text-[#FF6F4A] underline" data-testid={`resend-receipt-${idx}`}>Resend</button></div>) : (<Button size="sm" onClick={() => handleSendReceipt(reg.id)} disabled={sendingReceipt === reg.id || reg.payment_status !== 'completed'} className="bg-indigo-600 hover:bg-indigo-700 text-white text-[9px] px-1.5 py-0.5 h-5" data-testid={`send-receipt-${idx}`}>{sendingReceipt === reg.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Receipt'}</Button>)}</td>}
                      {visibleCols.date && <td className="py-2 px-3 text-slate-400 text-[10px]">{formatDate(reg.created_at)}</td>}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
